/**
 * RFC 4648 base32 alphabet without pad.
 */
const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";

/**
 * Converts a base32 string to an Uint8Array (RFC 4648).
 */
const base32Decode = (str) => {
  // Remove spaces (although they are not allowed by the spec, some issuers add them for readability).
  str = str.replace(/ /g, "");

  // Canonicalize to all upper case and remove padding if it exists.
  let end = str.length;
  while (str[end - 1] === "=") --end;
  str = (end < str.length ? str.substring(0, end) : str).toUpperCase();

  const buf = new ArrayBuffer(((str.length * 5) / 8) | 0);
  const arr = new Uint8Array(buf);
  let bits = 0;
  let value = 0;
  let index = 0;

  for (let i = 0; i < str.length; i++) {
    const idx = ALPHABET.indexOf(str[i]);
    if (idx === -1) throw new TypeError(`Invalid character found: ${str[i]}`);

    value = (value << 5) | idx;
    bits += 5;

    if (bits >= 8) {
      bits -= 8;
      arr[index++] = value >>> bits;
    }
  }

  return arr;
};

/**
 * Converts an integer to an Uint8Array.
 */
const uintDecode = (num) => {
  const buf = new ArrayBuffer(8);
  const arr = new Uint8Array(buf);
  let acc = num;

  for (let i = 7; i >= 0; i--) {
    if (acc === 0) break;
    arr[i] = acc & 255;
    acc -= arr[i];
    acc /= 256;
  }

  return arr;
};

/**
 * Calculates an HMAC digest.
 */
const hmacDigest = async (keyUint8Array, messageUint8Array, algorithm = "SHA-1") => {
  // Import the secretBytes as a CryptoKey
  const cryptoKey = await window.crypto.subtle.importKey(
      "raw",
      keyUint8Array,
      { name: "HMAC", hash: algorithm },
      false,
      ["sign"]
  );

  // Sign the message with HMAC and the CryptoKey
  const signature = await window.crypto.subtle.sign(
      "HMAC",
      cryptoKey,
      messageUint8Array
  );

  return new Uint8Array(signature);
};

class Totp {
  // pass in the secret, code dom element, ticker dom element
  constructor(expiry = 30, length = 6) {
    this.expiry = expiry;
    this.length = length;
    // validate input
    if (this.length > 8 || this.length < 6) {
      throw "Error: invalid code length";
    }
  }

  async getOtp(secret, timestamp = new Date().getTime()) {
    const counter = Math.floor(timestamp / 1000 / this.expiry).toString().padStart(16, "0");
    const counterUInt8Array = uintDecode(counter);
    const secretUint8Array = base32Decode(secret);
    const digest = await hmacDigest(secretUint8Array, counterUInt8Array, "SHA-1");
    const offset = digest[digest.byteLength - 1] & 15;
    const otp =
      (((digest[offset] & 127) << 24) |
        ((digest[offset + 1] & 255) << 16) |
        ((digest[offset + 2] & 255) << 8) |
        (digest[offset + 3] & 255)) %
      10 ** this.length;

    return otp.toString().padStart(this.length, "0");
  }

  async poll(secret, callback) {
    const timestamp = new Date().getTime();
    const nextPollTime = ((Math.floor(timestamp / 1000 / this.expiry) + 1) * this.expiry) * 1000;
    const timeoutMillis = nextPollTime - timestamp;

    const pollId = this.pollId = setTimeout(() => {
      this.poll(secret, callback);
    }, timeoutMillis);

    const totp = await this.getOtp(secret);
    if (this.pollId === pollId) {
      const newTimeoutMillis = nextPollTime - new Date().getTime();
      callback(totp, newTimeoutMillis / 1000.0);
    }
  }

  start(secret, callback) {
    this.poll(secret, callback);
  }
}

if (window.location.search.length) {
  const urlParams = new URLSearchParams(window.location.search);
  window.seedName = urlParams.get('name');
  window.seedValue = urlParams.get('seed')
}

function start(seedName, seedValue) {
  window.seedName = seedName;
  window.seedValue = seedValue;
  _start();
}

function _start() {
  const seedName = window.seedName;
  const seedValue = window.seedValue;
  const nameElem = document.getElementById("name");
  const codeElem = document.getElementById("code");
  const progressElem = document.getElementById("progress");
  if (! (seedName && seedValue)) {
    nameElem.innerHTML = "<div>&nbsp;</div>";
    codeElem.innerHTML = "&nbsp;";
    return;
  }

  nameElem.innerHTML = `<div>${seedName}</div>`;
  progressElem.classList.add("progress-moving");

  const totp = new Totp();
  totp.start(seedValue, (value, expirySeconds) => {
      codeElem.innerHTML = value;
      document.documentElement.style.setProperty('--expiry', expirySeconds + 's');
      document.getElementById("progress").classList.toggle('restart');
  });
}

document.addEventListener("DOMContentLoaded", (event) => {
  _start();
});

window.addEventListener(
  "message",
  (event) => {
    if (!event.data) { return; }
    start(event.data.seedName, event.data.seedValue)
  }
);
